import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { invalidateCredentialVaultRecord } from "../../lib/credential-vault-invalidation";
import type { RunScope } from "./types";

/** Refreshes the provider's credential record once for each run that has
 * finished, whether it finished in the run list or in the open report. */
export function useRunCompletionRefresh({
  scope,
  completedRunId,
  completedReportId,
}: {
  scope: RunScope;
  completedRunId: string | null;
  completedReportId: string | null;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const refreshedRuns = useRef(new Set<string>());
  const {
    organizationBusinessUnitId,
    providerUserId,
    expectedAffiliationId,
    credentialViewId,
  } = scope;

  useEffect(() => {
    const finishedIds = [completedRunId, completedReportId].filter(
      (id): id is string => !!id && !refreshedRuns.current.has(id),
    );
    if (!finishedIds.length) {
      return;
    }
    finishedIds.forEach((id) => refreshedRuns.current.add(id));
    const readScope = {
      organizationBusinessUnitId,
      providerUserId,
      expectedAffiliationId,
      credentialViewId,
    };
    // Completion can attach evidence or record a blocked outcome before any
    // polling interval has fired. Refresh the exact record once per run.
    void queryClient.invalidateQueries({
      queryKey: trpc.psv.listRuns.infiniteQueryKey(readScope),
    });
    void queryClient.invalidateQueries({
      queryKey: trpc.psv.getProviderScreenings.queryKey(readScope),
    });
    void invalidateCredentialVaultRecord({
      queryClient,
      trpc,
      scope: { kind: "organization", ...readScope },
    });
    if (!credentialViewId) {
      void queryClient.invalidateQueries({
        queryKey: trpc.credentials.listVaultFilesForBu.queryKey(readScope),
      });
      void queryClient.invalidateQueries({
        queryKey: trpc.credentialFiles.getCredentialsFields.queryKey({
          organizationBusinessUnitId,
          switchUserId: providerUserId,
        }),
      });
      void queryClient.invalidateQueries({
        queryKey: trpc.credentialFiles.getFilesCheckPermission.queryKey({
          organizationBusinessUnitId,
          switchUserId: providerUserId,
        }),
      });
    }
  }, [
    completedRunId,
    completedReportId,
    organizationBusinessUnitId,
    providerUserId,
    expectedAffiliationId,
    credentialViewId,
    queryClient,
    trpc,
  ]);
}
